import { z } from "zod";

import { TokenResponse } from "@/types";
import axios from "axios";
import { RateLimitInfo, RequestConfig } from "@/types/api";

const DEFAULT_RETRY_CONFIG = {
  maxRetries: 3,
  baseDelay: 1000,
  maxDelay: 10000,
};

const TOKEN_SCHEMA = z.object({
  access_token: z.string(),
  expires_in: z.number(),
  token_type: z.string(),
});

function isPlainObject(value: any): value is Record<string, any> {
  return typeof value === "object" && value !== null && value.constructor === Object;
}

class ApiClient {
  private baseUrl: string;
  private defaultHeaders: HeadersInit;
  private tokenRetryCount: number;
  private rateLimitInfo: RateLimitInfo | null;
  private abortControllers: Map<string, AbortController>;
  private isRedirecting: boolean = false;

  constructor(config: { baseUrl: string; defaultHeaders?: HeadersInit }) {
    this.baseUrl = config.baseUrl;
    this.defaultHeaders = {
      "Content-Type": "application/json",
      "Accept": "application/json",
      // "X-Request-ID": crypto.randomUUID(),
      ...config.defaultHeaders,
    };
    this.tokenRetryCount = 0;
    this.rateLimitInfo = null;
    this.abortControllers = new Map();
  }

  private async delay(ms: number): Promise<void> {
    await new Promise(resolve => setTimeout(resolve, ms));
  }

  private getExponentialBackoff(attempt: number, config: RequestConfig["retryConfig"]): number {
    const { baseDelay = 1000, maxDelay = 10000 } = config || {};
    const delay = Math.min(baseDelay * Math.pow(2, attempt), maxDelay);
    return delay + Math.random() * 1000; // Add jitter
  }

  private buildUrl(endpoint: string, params?: Record<string, string>): string {
    const url = new URL(`${this.baseUrl}${endpoint}`);
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          url.searchParams.append(key, value);
        }
      });
    }
    return url.toString();
  }

  private sanitizeRequestBody(body: unknown): string | FormData | URLSearchParams {
    if (body instanceof FormData || body instanceof URLSearchParams) {
      return body;
    }

    if (body === null || body === undefined) {
      return JSON.stringify({});
    }

    // Handle string input
    if (typeof body === "string") {
      try {
        // Check if it's already a JSON string
        JSON.parse(body);
        return body;
      } catch {
        // If not a JSON string, convert it to one
        return JSON.stringify({ message: body });
      }
    }

    // Handle plain objects and arrays
    if (isPlainObject(body) || Array.isArray(body)) {
      // Remove undefined values and sanitize the data
      const sanitized = JSON.parse(JSON.stringify(body));
      return JSON.stringify(sanitized);
    }

    throw new Error("Invalid request body type");
  }

  private updateRateLimitInfo(headers: Headers): void {
    this.rateLimitInfo = {
      limit: parseInt(headers.get("X-RateLimit-Limit") || "0", 10),
      remaining: parseInt(headers.get("X-RateLimit-Remaining") || "0", 10),
      reset: parseInt(headers.get("X-RateLimit-Reset") || "0", 10),
    };
  }

  private tokenCache: {
    token: TokenResponse | null;
    expirationTime: number | null;
  } = {
      token: null,
      expirationTime: null
    };

  private async getAccessToken(): Promise<TokenResponse> {
    try {
      // Check if token is cached and valid
      if (
        this.tokenCache.token &&
        this.tokenCache.expirationTime &&
        Date.now() < this.tokenCache.expirationTime
      ) {
        return this.tokenCache.token;
      }

      // If expired or not present, fetch new token
      if (
        this.tokenCache.token &&
        this.tokenCache.expirationTime &&
        Date.now() >= this.tokenCache.expirationTime
      ) {
        await this.handleTokenExpiration();
        return this.tokenCache.token!;
      }

      const response = await fetch("/api/auth/token");

      if (!response.ok) {
        if (response.status === 401) {
          await this.handleTokenExpiration();
          throw new Error("Token expired");
        }
        throw new Error("Failed to fetch access token");
      }

      const data = await response.json();
      const validatedToken = TOKEN_SCHEMA.parse(data);

      // Calculate absolute expiration time and cache it
      const expirationTime = Date.now() + (validatedToken.expires_in * 1000);

      this.tokenCache = {
        token: validatedToken,
        expirationTime
      };

      return validatedToken;
    } catch (error) {
      console.error("Token fetch error:", error);
      if (error instanceof Error && error.message === "Token expired") {
        await this.handleTokenExpiration();
      }
      throw error;
    }
  }

  private async handleTokenExpiration() {
    this.tokenCache = {
      token: null,
      expirationTime: null
    };

    // Use window.location for client-side navigation
    if (typeof window !== "undefined" &&
      !window.location.pathname.includes("/api/auth/login") &&
      !window.location.pathname.includes("/api/auth/logout") &&
      !this.isRedirecting) {

      this.isRedirecting = true; // Set flag to prevent multiple redirects

      const currentParams = new URLSearchParams(window.location.search);

      // Use a direct redirect instead of router to avoid React state issues
      window.location.href = `/api/auth/login?${currentParams.toString()}`;
    }
  }

  public getRateLimitInfo(): RateLimitInfo | null {
    return this.rateLimitInfo;
  }

  public cancelRequest(requestId: string): void {
    const controller = this.abortControllers.get(requestId);
    if (controller) {
      controller.abort();
      this.abortControllers.delete(requestId);
    }
  }

  public cancelAllRequests(): void {
    this.abortControllers.forEach(controller => controller.abort());
    this.abortControllers.clear();
  }

  /**
   * Enhanced request method that supports FormData/file uploads
   */
  public async request<T>(
    endpoint: string,
    config: RequestConfig = {}
  ): Promise<T> {
    const {
      method = "GET",
      body,
      params,
      retryConfig = DEFAULT_RETRY_CONFIG,
      headers: configHeaders = {},
      signal: externalSignal,
      timeout = 80000,
      ...restConfig
    } = config;

    const requestId = crypto.randomUUID();
    const controller = new AbortController();
    this.abortControllers.set(requestId, controller);

    const timeoutId = setTimeout(() => {
      controller.abort();
    }, timeout);

    const signals = externalSignal
      ? [externalSignal, controller.signal]
      : [controller.signal];

    const combinedSignal = new AbortController().signal;
    signals.forEach(signal => {
      signal.addEventListener("abort", () => controller.abort());
    });

    try {
      const { access_token } = await this.getAccessToken();

      const headers = new Headers()

      // Only add default headers if not dealing with FormData
      const isFormData = body instanceof FormData;

      if (!isFormData) {
        // Add default headers (for non-FormData requests)
        Object.entries(this.defaultHeaders).forEach(([key, value]) => {
          headers.append(key, value);
        });
      } else {
        // For FormData, only add necessary headers, not content-type (browser sets it with boundary)
        headers.append("Accept", "application/json");
      }

      // Add authorization header
      headers.set("Authorization", `Bearer ${access_token}`);

      // Add config headers
      Object.entries(configHeaders).forEach(([key, value]) => {
        if (value) headers.set(key, value);
      });

      const url = this.buildUrl(endpoint, params);

      // Process request body based on type
      let requestBody: any = undefined;

      if (body) {
        if (body instanceof FormData || body instanceof URLSearchParams) {
          requestBody = body;
        } else {
          requestBody = this.sanitizeRequestBody(body);
        }
      }

      let attempt = 0;
      let lastError: Error | null = null;

      while (attempt <= retryConfig.maxRetries) {
        try {

          const response = await fetch(url, {
            ...restConfig,
            method,
            headers: Object.fromEntries(headers),
            body: requestBody,
            credentials: "include",
            signal: combinedSignal,
          });

          this.updateRateLimitInfo(response.headers);

          if (!response.ok) {
            const errorStatus = response.status;

            // Check if we should retry based on status code
            const shouldRetry =
              errorStatus >= 500 ||
              errorStatus === 429 ||
              errorStatus === 401;

            if (!shouldRetry || attempt === retryConfig.maxRetries) {
              // If we shouldn't retry or we've reached max retries
              const errorData = await response.json().catch(() => ({ message: "Unknown error" }));
              return Promise.resolve(new Error(errorData.message || `Request failed with status ${errorStatus}`));
            }

            // Retry after delay
            await this.delay(this.getExponentialBackoff(attempt, retryConfig));
            attempt++;
            continue;
          }

          // Parse response based on content type
          const contentType = response.headers.get("Content-Type") || "";

          if (contentType.includes("application/json")) {
            const data = await response.json();
            return data as T;
          } else if (contentType.includes("text/")) {
            const text = await response.text();
            return text as unknown as T;
          } else {
            const blob = await response.blob();
            return blob as unknown as T;
          }
        } catch (error) {
          lastError = error as Error;

          if (
            error instanceof Error &&
            error.name === "AbortError" ||
            attempt === retryConfig.maxRetries
          ) {
            throw error;
          }

          await this.delay(this.getExponentialBackoff(attempt, retryConfig));
          attempt++;
        }
      }

      if (lastError) {
        console.error(lastError);
        throw lastError;
      }

      throw new Error("Request failed after multiple attempts");
    } finally {
      clearTimeout(timeoutId);
      this.abortControllers.delete(requestId);
    }
  }

  /**
   * Specialized method for file uploads
   */
  /**
 * Specialized method for file uploads with detailed logging
 */
  public async uploadFile<T>(
    endpoint: string,
    file: File | Blob,
    fieldName: string = "file",
    additionalData: Record<string, any> = {},
    config: Omit<RequestConfig, "body"> = {}
  ): Promise<T> {
    console.log(`[API-CLIENT] Uploading file to: ${endpoint}`);

    // Create FormData and append the file
    const formData = new FormData();
    formData.append(fieldName, file, file instanceof File ? file.name : undefined);

    // Add any additional form fields
    Object.entries(additionalData).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        console.log(`[API-CLIENT] Adding form field: ${key}=${value}`);
        formData.append(key, typeof value === "object" ? JSON.stringify(value) : String(value));
      }
    });

    try {
      // Use the regular request method with FormData
      return this.request<T>(endpoint, {
        ...config,
        method: "POST",
        body: formData,
        // Important: No content-type header for FormData - browser sets it with boundary
        headers: {
          ...config.headers,
          // Explicitly remove content-type header if it exists
          // "Content-Type": "multipart/form-data"
        }
      });
    } catch (error) {
      console.error(`[API-CLIENT] File upload failed: ${error.message}`);
      throw error;
    }

  }
};

  // Create API client instance
  export const apiClient = new ApiClient({
    baseUrl: process.env.NEXT_PUBLIC_API_BASE_URL || "http://localhost:8000",
    defaultHeaders: {
      // "X-Client-Version": process.env.NEXT_PUBLIC_APP_VERSION || "1.0.0",
    },
  });